import { useScienceTutorSection } from './scienceTutorContext';
import { ArrowUp, CheckCircle2, FlaskConical, RotateCcw, Sparkles, XCircle } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';

import {
  MAX_PLAN_STEPS,
  MIXTURE_CASES,
  MIXTURE_STEPS,
  componentName,
  getMixtureCase,
  joinNames,
  mixtureStepLabel,
  runMixturePlan,
  validMixturePlans,
  type MixtureStepId,
} from './separationPlanner';
import { PlanBuilder, ResultBanner, StaleResultNotice, VesselView } from './SeparationPlannerVisuals';

const OTHER_ORDERS_SHOWN = 4;

function planText(plan: MixtureStepId[]): string {
  return plan.map(mixtureStepLabel).join(' → ');
}

export function SeparationPlannerMixture() {
  const [caseId, setCaseId] = useState(MIXTURE_CASES[0].id);
  const [plan, setPlan] = useState<MixtureStepId[]>([]);
  // The plan the model last ran. The plan stays editable; the result keeps describing this snapshot.
  const [ranPlan, setRanPlan] = useState<MixtureStepId[] | null>(null);
  const [viewStep, setViewStep] = useState(-1);
  const [showWorking, setShowWorking] = useState(false);
  const planRef = useRef<HTMLElement>(null);
  useScienceTutorSection('SeparationPlannerMixture', { caseId, plan, ranPlan, viewStep, showWorking });

  const mixture = getMixtureCase(caseId) ?? MIXTURE_CASES[0];
  const result = useMemo(() => (ranPlan ? runMixturePlan(mixture.id, ranPlan) : null), [ranPlan, mixture.id]);
  const validPlans = useMemo(() => (ranPlan ? validMixturePlans(mixture.id) : []), [ranPlan, mixture.id]);

  const reset = (nextCaseId = mixture.id) => {
    setCaseId(nextCaseId);
    setPlan([]);
    setRanPlan(null);
    setViewStep(-1);
    setShowWorking(false);
  };

  const run = () => {
    setRanPlan([...plan]);
    setViewStep(-1);
  };

  const changePlan = () => planRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });

  const shownVessel = result && viewStep >= 0 ? result.outcomes[viewStep]?.after ?? mixture.start : mixture.start;
  const shownCaption = result && viewStep >= 0
    ? `Container after step ${viewStep + 1} (${mixtureStepLabel(result.outcomes[viewStep].stepId)})`
    : 'Starting mixture';
  const planKey = plan.join('>');
  const ranKey = ranPlan?.join('>') ?? null;
  const stale = ranKey !== null && ranKey !== planKey;
  const otherOrders = validPlans.filter((item) => item.join('>') !== ranKey).slice(0, OTHER_ORDERS_SHOWN);

  return (
    <section className="mt-6 grid gap-6 xl:grid-cols-[320px_minmax(0,1fr)]" aria-label="Mixture separation">
      <aside className="min-w-0 rounded-[2rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft sm:p-6">
        <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-sky">Year 7 · Separating mixtures</p>
        <label className="mt-3 block text-sm font-extrabold text-ink" htmlFor="separation-mixture">
          Choose a mixture
        </label>
        <select
          id="separation-mixture"
          value={mixture.id}
          onChange={(event) => reset(event.target.value)}
          className="mt-2 w-full rounded-2xl border border-hairline bg-surface px-3 py-2 text-sm font-extrabold text-ink"
        >
          {MIXTURE_CASES.map((item, index) => (
            <option key={item.id} value={item.id}>
              {index + 1}. {item.title}
            </option>
          ))}
        </select>
        <h2 className="mt-5 text-xl font-extrabold text-ink">{mixture.title}</h2>
        <p className="mt-2 text-sm font-medium leading-6 text-slate2">{mixture.context}</p>
        <p className="mt-3 rounded-2xl bg-wash-sunshine px-4 py-3 text-sm font-extrabold text-ink">
          Goal: {mixture.goal}
        </p>
        <div className="mt-5 rounded-[1.5rem] border border-hairline bg-surface p-4">
          <VesselView items={shownVessel} caption={shownCaption} />
        </div>
        <p className="mt-2 text-xs font-medium leading-5 text-slate2">
          Circles are drawn by the model from the container state. Not to scale.
        </p>
      </aside>

      <div className="min-w-0 space-y-6">
        <article ref={planRef} className="scroll-mt-6 rounded-[2rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft sm:p-6 lg:p-8">
          <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-coral">Plan the order</p>
          <h2 className="mt-2 text-2xl font-extrabold text-ink">Which step comes first?</h2>
          <p className="mt-2 max-w-3xl font-medium leading-7 text-slate2">
            Predict a sequence. Each step uses one property, acts on the main container and sets one
            fraction aside. The model then runs your plan step by step.
          </p>
          <div className="mt-5">
            <PlanBuilder
              steps={MIXTURE_STEPS}
              plan={plan}
              maxSteps={MAX_PLAN_STEPS}
              onChange={setPlan}
              noun="step"
            />
          </div>
          <div className="mt-5 flex flex-wrap gap-3">
            <button
              type="button"
              disabled={plan.length === 0 || (ranKey !== null && !stale)}
              onClick={run}
              className="inline-flex items-center gap-2 rounded-full bg-ink px-5 py-3 text-sm font-extrabold text-white disabled:cursor-not-allowed disabled:opacity-40"
            >
              <Sparkles className="h-4 w-4" aria-hidden="true" /> Run my plan
            </button>
            <button
              type="button"
              onClick={() => reset()}
              className="inline-flex items-center gap-2 rounded-full border border-hairline px-5 py-3 text-sm font-extrabold text-ink hover:bg-surface"
            >
              <RotateCcw className="h-4 w-4" aria-hidden="true" /> Start again
            </button>
          </div>
        </article>

        {result ? (
          <article className="space-y-5 rounded-[2rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft sm:p-6 lg:p-8">
            {stale ? <StaleResultNotice onRun={run} runLabel="Run my new plan" /> : null}
            <ResultBanner
              success={result.status === 'success'}
              title={result.status === 'success' ? 'Your plan works' : 'Not yet — the model found a problem'}
            >
              <p>{result.summary}</p>
              <p className="text-sm font-bold text-ink-soft">{mixture.teachingPoint}</p>
              {result.status === 'success' && otherOrders.length > 0 ? (
                <p className="text-sm">
                  <strong className="text-ink">Other orders that also work:</strong>{' '}
                  {otherOrders.map(planText).join('; ')}.
                </p>
              ) : null}
              {result.status === 'success' && otherOrders.length === 0 ? (
                <p className="text-sm">The model found no other working order for this mixture.</p>
              ) : null}
              {result.status !== 'success' ? (
                <button
                  type="button"
                  onClick={changePlan}
                  className="inline-flex items-center gap-2 rounded-full border border-hairline bg-white px-4 py-2 text-sm font-extrabold text-ink hover:bg-surface"
                >
                  <ArrowUp className="h-4 w-4" aria-hidden="true" /> Change my plan
                </button>
              ) : null}
            </ResultBanner>

            <div>
              <h3 className="text-lg font-extrabold text-ink">Step by step</h3>
              <ol className="mt-3 grid gap-3">
                {result.outcomes.map((outcome, index) => (
                  <li
                    key={outcome.stepId}
                    className={`rounded-2xl border p-4 ${outcome.status === 'worked' ? 'border-brand-mint/40 bg-white' : 'border-brand-coral/40 bg-wash-coral'}`}
                  >
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <p className="flex items-center gap-2 text-sm font-extrabold text-ink">
                        {outcome.status === 'worked' ? (
                          <CheckCircle2 className="h-4 w-4 text-brand-mint" aria-hidden="true" />
                        ) : (
                          <XCircle className="h-4 w-4 text-brand-coral" aria-hidden="true" />
                        )}
                        Step {index + 1}: {mixtureStepLabel(outcome.stepId)} — {outcome.status === 'worked' ? 'works' : 'fails here'}
                      </p>
                      {outcome.status === 'worked' ? (
                        <button
                          type="button"
                          aria-pressed={viewStep === index}
                          onClick={() => setViewStep(index)}
                          className="rounded-full border border-hairline px-3 py-1 text-xs font-extrabold text-ink hover:bg-surface"
                        >
                          Show container after step {index + 1}
                        </button>
                      ) : null}
                    </div>
                    <p className="mt-2 text-sm font-medium leading-6 text-slate2">{outcome.message}</p>
                    {outcome.fractions.length > 0 ? (
                      <ul className="mt-2 flex flex-wrap gap-2">
                        {outcome.fractions.map((fraction) => (
                          <li
                            key={fraction.label}
                            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-extrabold ${
                              fraction.how === 'collected' ? 'bg-wash-mint text-ink' : 'bg-surface text-slate2'
                            }`}
                          >
                            <FlaskConical className="h-3.5 w-3.5" aria-hidden="true" />
                            {fraction.how === 'collected' ? 'Collected' : 'Lost'}: {joinNames(fraction.componentIds.map(componentName))} — {fraction.label}
                          </li>
                        ))}
                      </ul>
                    ) : null}
                  </li>
                ))}
              </ol>
            </div>

            {result.status !== 'success' ? (
              <div>
                <button
                  type="button"
                  onClick={() => setShowWorking(true)}
                  disabled={showWorking}
                  className="rounded-full border border-hairline px-4 py-2 text-sm font-extrabold text-ink hover:bg-surface disabled:opacity-60"
                >
                  Show a working order
                </button>
                {showWorking ? (
                  <p className="mt-3 rounded-2xl bg-surface px-4 py-3 text-sm font-bold text-ink">
                    One working order: {planText(mixture.referencePlan)}.
                  </p>
                ) : null}
              </div>
            ) : null}
          </article>
        ) : null}
      </div>
    </section>
  );
}
